import { useState } from 'react';
import PropTypes from 'prop-types';
import { breakpoints, Button, PageBlock } from '@etg/wings';
import { ContentWrapper, DangerousHtmlContent, Notification, variants } from '@eti/components';
import { Collection, DevBar } from '@eti/debug';
import { useHeightContext, useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { css, etiColors, mediaQueries } from '@eti/styles';
import {
  BoundNew,
  BrandedFare,
  BundleProduct,
  ExtraProduct,
  TravelerNew,
  TravelerPrice,
  TripChange,
  TripCharacteristics,
} from '@eti/types';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Media from 'react-media';
import { useMediaQuery } from 'usehooks-ts';
import { analytics, price as priceUtil } from '@eti/utils';
import { Tier } from '@eti/schema-types';
import SelectedTrip from '../../result/components/v1/SelectedTrip';
import StepIndicator from '../../../common/step-indicator/BookingFlowStepIndicator';
import { SELECT_FLIGHT } from '../../../constants/bookingFlowSteps';
import { isDev } from '../../../common/debug/utils';
import { DebugCommonComponents, DebugGraphQlResponses } from '../../../common/debug/components';
import { routes } from '../../../constants/routesAndApisConstants';
import { getTicketingCarrierCodes } from '../../traveler-details/utils/carriers';
import { getPriceNotificationContent } from '../../../utils/notification';
import { MULTI_STOP, ONE_WAY, RETURN } from '../../../constants/tripTypesConstants';
import { getUniqueMarketingCarriers } from '../../../utils/carriers';
import DifferentChargingCurrencyNotification from '../../../widgets/different-charging-currency-notification/DifferentChargingCurrencyNotification';
import { getTravelerPrices } from '../../../utils/travelers';
import { useScrollTop } from '../../../common/custom-hooks/scrolling';
import { useScrollIntoVisibleViewport } from '../../../utils/useScrollIntoVisibleViewport';
import BrandedFaresV1 from './v1/BrandedFares';
import BrandedFaresV2, { getDefaultAndRecommendedFare } from './v2/BrandedFares';
import BrandedFaresV3 from './v3/branded-fares/BrandedFares';
import AncillaryBundleSelection from './AncillaryBundleSelection';
import TripDetailsTitle from './v3/TripDetailsTitle';
import SelectedTripCard from './selected-trip-card/v3/SelectedTripCard';
import StickyFooter from './sticky-footer/StickyFooter';
import { SellingPoint } from './selling-point/SellingPoint';
import { GFSPartner } from './v3/branded-fares/constants';

const bottomContentWrapperStyles = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  margin-top: 24px;

  > * {
    width: 100%;
  }
`;

const proceedButtonWrapperStyles = css`
  display: flex;
  justify-content: center;
`;

export const buttonStyles = css`
  width: 100%;

  @media ${mediaQueries.medium.up} {
    width: auto;
  }
`;

const wrapperStyles = (
  shouldDisplayAncillaryBundle,
  shouldDisplayEtgFares,
  headerAndStepIndicatorHeight,
  shouldShowStickyFooter,
) => css`
  background-color: ${shouldDisplayAncillaryBundle && !shouldDisplayEtgFares
    ? etiColors.white
    : etiColors.grayScale[4]};
  display: flex;
  flex-direction: column;
  height: 100%;
  ${shouldShowStickyFooter && `min-height: calc(100vh - ${headerAndStepIndicatorHeight}px);`}
`;

const contentStyles = css`
  margin: 2rem auto 1rem;
`;

const headingStyles = css`
  color: ${etiColors.black};
  font-size: 1.375rem;
  text-align: center;
`;

const sectionGFSWrapperNoBrandedFaresStyles = css`
  margin: 10px auto 0;

  @media ${mediaQueries.medium.up} {
    margin: 24px auto 0;
  }
`;

const sectionTitleWrapperStyles = css`
  margin: 10px auto;

  @media ${mediaQueries.medium.up} {
    margin: 24px auto 0;
  }
`;

const getInitialBrandedFareSelectionKey = (brandedFares, selectionKey) => () => {
  if (!brandedFares?.length) {
    return null;
  }
  if (brandedFares.some((brandedFare) => brandedFare.selectionKey === selectionKey)) {
    return selectionKey;
  }
  return brandedFares[0].selectionKey;
};

const getBasicBundleOption = (bundleData) => {
  return bundleData?.options?.find((option) => option.type === Tier.Basic) ?? {};
};

const YourSelectedTripPage = ({
  addTrip,
  availableExtraProducts,
  bounds,
  brandedFares = [],
  bundleData,
  isSelectedTripRefetching,
  hasBookingButton,
  includedCabinBaggage,
  includedCheckedBaggage,
  isMultiStop,
  isSearchLoading,
  isSelectTripLoading,
  numberOfTravelers,
  paymentMethodPrices,
  redirectToTravelerDetails,
  selectedBundleId,
  selectedBundlePrice,
  selectedExtraProducts,
  selectionKey,
  selectTrip,
  setSelectedBundleOption,
  shouldHideSelectedTrip,
  systems,
  travelerPrices,
  travelerPricesWithoutPaymentDiscounts,
  tripChange,
  tripCharacteristics,
  tripTravelers,
  type,
  voucherAmount,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { p } = useProperty();
  const [selectedBrandedFareSelectionKey, setSelectedBrandedFareSelectionKey] = useState(
    getInitialBrandedFareSelectionKey(brandedFares, selectionKey),
  );
  const [showPriceNotification, setShowPriceNotification] = useState(Boolean(tripChange?.price));

  const { partner } = useSiteContext();

  const adultTravelerArray = tripTravelers?.[0] ? [{ ...tripTravelers?.[0] }] : [];

  const selectedTripPriceArray = getTravelerPrices(adultTravelerArray, travelerPrices);
  const selectedTripPricePerAdult = selectedTripPriceArray?.[0]?.totalPrice;

  const isSelectedTripCardEnabled = p('YourSelectedTrip.Page.SelectedTripCard.V3.Enabled');
  const isSelectedTripCardEnabledForBrandedFareV3 = p(
    'YourSelectedTrip.Page.BrandedFares.V3.SelectedTripCard.V3.Enabled',
  );
  const isSelectedTripCardEnabledForETGFares = p(
    'YourSelectedTrip.Page.ETGFares.SelectedTripCard.V3.Enabled',
  );
  const isBrandedFaresEnabled = p('BrandedFares.Enabled');

  const shouldDisplayBrandedFares = isBrandedFaresEnabled && brandedFares?.length > 0;

  const shouldDisplayAncillaryBundle =
    !shouldDisplayBrandedFares && p('YourSelectedTrip.AncillaryBundle.Enabled') && bundleData;

  const shouldDisplayEtgFares = shouldDisplayAncillaryBundle && p('ETG.Fares.Enabled');

  const brandedFaresVersion = p('YourSelectedTrip.Page.BrandedFares.Version');

  const { defaultSelectedFareKey, recommendedFareKey } =
    (shouldDisplayBrandedFares && getDefaultAndRecommendedFare(travelerPrices, brandedFares)) || [];

  const yourSelectedTripPageVersion = p('YourSelectedTrip.Page.Version');

  const isYourSelectedTripPageV2Enabled = yourSelectedTripPageVersion === 2;
  const isYourSelectedTripPageV1Enabled = yourSelectedTripPageVersion === 1;

  const displayProceedButtonCommonCondition =
    !(brandedFaresVersion === 3 || shouldDisplayEtgFares) ||
    (shouldDisplayAncillaryBundle && !shouldDisplayEtgFares);

  const eitherETGFaresOrBrandedFaresEnableAndAvailable =
    (isSelectedTripCardEnabledForBrandedFareV3 && shouldDisplayBrandedFares) ||
    (isSelectedTripCardEnabledForETGFares &&
      (shouldDisplayEtgFares || shouldDisplayAncillaryBundle));

  const shouldDisplaySelectedTripCard =
    (eitherETGFaresOrBrandedFaresEnableAndAvailable ||
      (!shouldDisplayBrandedFares && !shouldDisplayEtgFares && !shouldDisplayAncillaryBundle)) &&
    isSelectedTripCardEnabled;

  const shouldDisplayProceedButton =
    displayProceedButtonCommonCondition ||
    (displayProceedButtonCommonCondition && !isBrandedFaresEnabled);

  const basicBundleOption = getBasicBundleOption(bundleData);

  const callSelectTripAndProceed = async (
    bundlePrice,
    bundleId,
    isFareSelected,
    productIdsToPurchase,
  ) => {
    if (isFareSelected) {
      await selectTrip({
        variables: {
          selectionKey: selectedBrandedFareSelectionKey || defaultSelectedFareKey || selectionKey,
          ...(bundlePrice && {
            bundleProductSelection: [{ bundleId: bundleData.id, optionId: bundleId }],
          }),
          extraProductSelection: productIdsToPurchase,
        },
      });
    }
    return history.push(routes.TRAVELER_DETAILS);
  };

  const handleProceedButtonClick = (productIdsToPurchase) => {
    const brandedFareSelected =
      shouldDisplayBrandedFares &&
      (selectedBrandedFareSelectionKey || defaultSelectedFareKey || selectionKey);
    const isFareSelected = brandedFareSelected || Boolean(selectedBundleId);
    callSelectTripAndProceed(
      selectedBundlePrice,
      selectedBundleId,
      isFareSelected,
      productIdsToPurchase,
    );
  };

  const handleSkipStepButtonClick = () => {
    callSelectTripAndProceed(
      basicBundleOption.priceRaw,
      basicBundleOption.id,
      true,
      selectedExtraProducts,
    );
  };

  const shouldUseValidation = p(
    'IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled',
  );

  const headingText =
    t(`YourSelectedTrip.BookingFlow.PartnerName.${partner}`) ||
    t('YourSelectedTrip.Page.NoPartner.Heading');

  let BrandedFares;
  switch (brandedFaresVersion) {
    case 3:
      BrandedFares = BrandedFaresV3;
      break;
    case 2:
      BrandedFares = BrandedFaresV2;
      break;
    default:
      BrandedFares = BrandedFaresV1;
      break;
  }

  analytics.useLogMouseFlowTagEffect(
    'Branded Fares',
    shouldDisplayBrandedFares && brandedFaresVersion === 2,
  );

  const isSelectTripEnabled = !shouldHideSelectedTrip && bounds?.length > 0;

  const regexp = new RegExp(`^${GFSPartner}`);
  const isPartnerGoogleFlights = regexp.test(partner);
  const isMobileDevice = useMediaQuery(`(max-width: ${breakpoints._768})`);
  const shouldHideUniqueSellingPoint = shouldDisplaySelectedTripCard && !isMobileDevice;

  const { headerHeightContext } = useHeightContext();
  const stepIndicatorHeight = 120;
  const headerAndStepIndicatorHeight = headerHeightContext + stepIndicatorHeight;
  useScrollIntoVisibleViewport({ paddingTop: 10, paddingBottom: 16 });

  const shouldShowStickyFooter =
    (isBrandedFaresEnabled &&
      brandedFaresVersion === 3 &&
      brandedFares.length === 0 &&
      !shouldDisplayAncillaryBundle) ||
    (!shouldDisplayAncillaryBundle && !shouldDisplayEtgFares && !isBrandedFaresEnabled);

  if (
    shouldHideSelectedTrip &&
    !shouldDisplayBrandedFares &&
    !bundleData &&
    !isPartnerGoogleFlights &&
    !shouldDisplaySelectedTripCard
  ) {
    history.push(routes.TRAVELER_DETAILS);
  }
  useScrollTop();
  return (
    <>
      <Helmet title={t('PageTitle.rf.your-selected-trip.action')} />
      <StepIndicator currentStep={SELECT_FLIGHT} />
      <section
        className={wrapperStyles(
          shouldDisplayAncillaryBundle,
          shouldDisplayEtgFares,
          headerAndStepIndicatorHeight,
          shouldShowStickyFooter,
        )}
      >
        {p('IbeClient.DifferentChargingCurrencyNotification.Enabled') && (
          <ContentWrapper>
            <DifferentChargingCurrencyNotification className={contentStyles} />
          </ContentWrapper>
        )}
        {!shouldHideSelectedTrip && (
          <ContentWrapper className={contentStyles}>
            <h3 className={headingStyles} data-testid="yourSelectedTrip-heading">
              {headingText}
            </h3>
          </ContentWrapper>
        )}
        {showPriceNotification && (
          <ContentWrapper className={contentStyles}>
            <Notification
              className="priceNotification"
              closeButtonDataTestId="travelerDetails-priceChangeCloseButton-button"
              dataTestId="travelerDetails-priceChange-TripNotification"
              onClose={() => setShowPriceNotification(false)}
            >
              <DangerousHtmlContent
                content={getPriceNotificationContent({
                  tripChange,
                  higherText: t('Notification.PriceIncrease.Message'),
                })}
              />
            </Notification>
          </ContentWrapper>
        )}
        {isSelectTripEnabled && (
          <PageBlock as="section" data-testid="resultPage-selectedTrip-wrapper" withPaddingY>
            <SelectedTrip
              addTrip={addTrip}
              availableExtraProducts={availableExtraProducts}
              bounds={bounds}
              hasBookingButton={hasBookingButton}
              includedCheckedBaggage={includedCheckedBaggage}
              isMultiStop={isMultiStop}
              isSearchLoading={isSearchLoading}
              isSelectTripLoading={isSelectTripLoading}
              isSelectedTripRefetching={isSelectedTripRefetching}
              numberOfTravelers={numberOfTravelers}
              paymentMethodPrices={paymentMethodPrices}
              redirectToTravelerDetails={redirectToTravelerDetails}
              selectionKey={selectionKey}
              shouldDisplayBrandedFares={shouldDisplayBrandedFares}
              systems={systems}
              travelerPrices={travelerPrices}
              travelerPricesWithoutPaymentDiscounts={travelerPricesWithoutPaymentDiscounts}
              tripCharacteristics={tripCharacteristics}
              tripTravelers={tripTravelers}
              voucherAmount={voucherAmount}
            />
          </PageBlock>
        )}
        {isYourSelectedTripPageV2Enabled && (
          <ContentWrapper className={sectionGFSWrapperNoBrandedFaresStyles}>
            <TripDetailsTitle bounds={bounds} />
            {shouldDisplaySelectedTripCard && (
              <SelectedTripCard
                availableExtraProducts={availableExtraProducts}
                bounds={bounds}
                includedCabinBaggage={includedCabinBaggage}
                includedCheckedBaggage={includedCheckedBaggage}
                selectedTripPricePerAdult={selectedTripPricePerAdult}
              />
            )}
          </ContentWrapper>
        )}

        {isYourSelectedTripPageV1Enabled && shouldDisplayEtgFares && (
          <ContentWrapper className={sectionTitleWrapperStyles}>
            <TripDetailsTitle bounds={bounds} />
          </ContentWrapper>
        )}

        {shouldDisplayBrandedFares && (
          <section>
            <Media query={mediaQueries.medium.only}>
              {(isMediumScreenWidth) => (
                <BrandedFares
                  availableExtraProducts={availableExtraProducts}
                  bounds={bounds}
                  brandedFares={brandedFares}
                  defaultSelectedFareKey={defaultSelectedFareKey}
                  discountAmount={voucherAmount?.value ?? 0}
                  handleProceedButtonClick={handleProceedButtonClick}
                  isMediumScreenWidth={isMediumScreenWidth}
                  isSelectTripEnabled={isSelectTripEnabled}
                  isSelectTripLoading={isSelectTripLoading}
                  isSelectedTripRefetching={isSelectedTripRefetching}
                  marketingCarrier={getUniqueMarketingCarriers(bounds)[0]}
                  partner={partner}
                  paymentMethodPrices={paymentMethodPrices}
                  recommendedFareKey={recommendedFareKey}
                  selectedBrandedFareSelectionKey={selectedBrandedFareSelectionKey}
                  selectedTripPricePerAdult={selectedTripPricePerAdult}
                  setSelectedBrandedFareSelectionKey={setSelectedBrandedFareSelectionKey}
                  ticketingCarrierCodes={getTicketingCarrierCodes(bounds)}
                  travelerPrices={travelerPrices}
                  travelerPricesWithoutPaymentDiscounts={travelerPricesWithoutPaymentDiscounts}
                  tripTravelers={tripTravelers}
                  tripType={type}
                />
              )}
            </Media>
          </section>
        )}
        {shouldDisplayAncillaryBundle && (
          <AncillaryBundleSelection
            ancillaryBundleId={bundleData.id}
            availableExtraProducts={availableExtraProducts}
            bounds={bounds}
            discountAmount={voucherAmount?.value ?? 0}
            discountPercentage={bundleData?.discountPercentage}
            handleProceedButtonClick={() => handleProceedButtonClick(selectedExtraProducts)}
            onSkipStepClick={handleSkipStepButtonClick}
            options={bundleData?.options}
            paymentMethodPrices={paymentMethodPrices}
            selectedBundleId={selectedBundleId}
            selectedExtraProducts={selectedExtraProducts}
            setSelectedBundleOption={setSelectedBundleOption}
            shouldDisplayEtgFares={shouldDisplayEtgFares}
            travelerPrices={travelerPrices}
            travelerPricesWithoutDiscounts={travelerPricesWithoutPaymentDiscounts}
            tripTravelers={tripTravelers}
            tripType={type}
          />
        )}
        {shouldDisplayProceedButton && (
          <section className={bottomContentWrapperStyles} data-testid="continue-button-wrapper">
            <ContentWrapper className={proceedButtonWrapperStyles}>
              <Button
                className={buttonStyles}
                data-testid="yourSelectedTrip-proceedButton"
                isDisabled={
                  shouldUseValidation ? shouldDisplayAncillaryBundle && !selectedBundleId : false
                }
                onClick={() => handleProceedButtonClick(selectedExtraProducts)}
                variant={variants.PRIMARY}
              >
                {t('YourSelectedTrip.Page.ProceedButton.Label')}
              </Button>
            </ContentWrapper>
          </section>
        )}
      </section>
      <section>
        {shouldShowStickyFooter && (
          <StickyFooter
            bounds={bounds}
            discountAmount={voucherAmount?.value ?? 0}
            isSelectTripLoading={isSelectTripLoading}
            onClick={() => handleProceedButtonClick(selectedExtraProducts)}
            paymentMethodPrices={paymentMethodPrices}
            totalPrice={priceUtil.formatPrice(selectedTripPricePerAdult)}
            travelerPrices={travelerPrices}
            travelerPricesWithoutDiscounts={travelerPricesWithoutPaymentDiscounts}
            tripTravelers={tripTravelers}
          />
        )}

        {!shouldHideUniqueSellingPoint && (
          <SellingPoint shouldDisplayMainUsp={!shouldHideSelectedTrip} />
        )}
      </section>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugGraphQlResponses />
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </>
  );
};

YourSelectedTripPage.propTypes = {
  addTrip: PropTypes.func,
  availableExtraProducts: PropTypes.arrayOf(ExtraProduct),
  bounds: PropTypes.arrayOf(BoundNew),
  brandedFares: PropTypes.arrayOf(BrandedFare),
  bundleData: BundleProduct,
  hasBookingButton: PropTypes.bool,
  includedCabinBaggage: PropTypes.shape({
    includedPersonalItem: PropTypes.bool, // eslint-disable-line
    size3d: PropTypes.shape({
      height: PropTypes.number,
      length: PropTypes.number,
      width: PropTypes.number,
    }),
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  includedCheckedBaggage: PropTypes.shape({
    pieces: PropTypes.number,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  isMultiStop: PropTypes.bool,
  isSearchLoading: PropTypes.bool,
  isSelectedTripRefetching: PropTypes.bool,
  isSelectTripLoading: PropTypes.bool,
  numberOfTravelers: PropTypes.number,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.shape({
        value: PropTypes.number,
      }),
    }),
  ),
  redirectToTravelerDetails: PropTypes.func,
  selectedBundleId: PropTypes.string,
  selectedBundlePrice: PropTypes.number,
  selectedExtraProducts: PropTypes.arrayOf(PropTypes.number),
  selectionKey: PropTypes.string,
  selectTrip: PropTypes.func,
  setSelectedBundleOption: PropTypes.func,
  shouldHideSelectedTrip: PropTypes.bool,
  systems: PropTypes.arrayOf(PropTypes.string),
  travelerPrices: PropTypes.arrayOf(TravelerPrice),
  travelerPricesWithoutPaymentDiscounts: PropTypes.arrayOf(TravelerPrice),
  tripChange: TripChange,
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
  tripTravelers: PropTypes.arrayOf(TravelerNew),
  type: PropTypes.oneOf([MULTI_STOP, ONE_WAY, RETURN]),
  voucherAmount: PropTypes.shape({
    value: PropTypes.number,
  }),
};

export default YourSelectedTripPage;
